import React, { useState, useMemo } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  Switch,
  Typography,
  Card,
  message,
  Popconfirm,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  EditOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { useAccounts } from '../../hooks/useAccounting';
import { AccountItem } from '../../services/accountingService';

const { Title, Text } = Typography;
const { Option } = Select;

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  ASSET: { label: 'Activo', color: 'blue' },
  LIABILITY: { label: 'Pasivo', color: 'red' },
  EQUITY: { label: 'Patrimonio', color: 'purple' },
  REVENUE: { label: 'Ingresos', color: 'green' },
  EXPENSE: { label: 'Gastos', color: 'orange' },
  COST_OF_SALES: { label: 'Costo de Ventas', color: 'volcano' },
  PRODUCTION_COST: { label: 'Costos de Producción', color: 'cyan' },
  MEMORANDUM: { label: 'Cuentas de Orden', color: 'default' },
};

export const calculateAccountLevel = (code: string): number => {
  const len = (code || '').trim().length;
  if (len === 1) return 1; // Clase
  if (len === 2) return 2; // Grupo
  if (len <= 4) return 3; // Cuenta
  if (len <= 6) return 4; // Subcuenta
  return 5; // Auxiliar
};

const ChartOfAccountsView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  // SWR Hook for reactive chart of accounts
  const {
    accounts,
    isLoading: accountsLoading,
    isValidating: accountsValidating,
    mutate: mutateAccounts,
  } = useAccounts({
    company_id: company.current.id,
  });

  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountItem | null>(null);
  const [form] = Form.useForm();
  const [seeding, setSeeding] = useState(false);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      const matchesSearch =
        acc.code.toLowerCase().includes(searchText.toLowerCase()) ||
        acc.name.toLowerCase().includes(searchText.toLowerCase());
      const matchesType = !selectedType || acc.type === selectedType;
      const matchesLevel = selectedLevel === null || acc.level === selectedLevel;
      return matchesSearch && matchesType && matchesLevel;
    });
  }, [accounts, searchText, selectedType, selectedLevel]);

  const handleOpenCreateModal = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({
      nature: 'DEBIT',
      is_active: true,
      requires_third_party: false,
      requires_cost_center: false,
      level: 4,
      type: 'ASSET',
    });
    setModalVisible(true);
  };

  const handleOpenEditModal = (account: AccountItem) => {
    setEditingAccount(account);
    const resolvedLevel = account.level || calculateAccountLevel(account.code);
    form.setFieldsValue({
      code: account.code,
      name: account.name,
      type: account.type,
      level: resolvedLevel,
      parent_id: account.parent_id,
      nature: account.nature,
      requires_third_party: account.requires_third_party,
      requires_cost_center: account.requires_cost_center,
      is_active: account.is_active,
    });
    setModalVisible(true);
  };

  const handleSaveAccount = async () => {
    try {
      const values = await form.validateFields();
      const code = values.code || editingAccount?.code || '';
      const level = values.level || editingAccount?.level || calculateAccountLevel(code);

      const payload = {
        ...values,
        code,
        level,
        company_id: company.current.id,
      };

      if (editingAccount?.id) {
        await securePost('/accounting/save-account', { ...payload, id: editingAccount.id });
        message.success('Cuenta contable actualizada con éxito.');
      } else {
        await securePost('/accounting/save-account', payload);
        message.success('Cuenta contable creada con éxito.');
      }

      setModalVisible(false);
      await mutateAccounts();
    } catch (err: any) {
      console.error('Error saving account:', err);
      message.error(err?.message || 'Error al guardar la cuenta contable.');
    }
  };

  const handleSeedPUC = async () => {
    if (!company.current.id) return;
    setSeeding(true);
    try {
      await securePost('/accounting/seed-puc', { company_id: company.current.id });
      message.success('Plan Único de Cuentas (PUC) estándar inicializado correctamente.');
      await mutateAccounts();
    } catch (err) {
      console.error('Error seeding PUC:', err);
      message.error('No se pudo inicializar el PUC.');
    } finally {
      setSeeding(false);
    }
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      render: (code: string, record: AccountItem) => {
        const isHeader = record.level <= 2;
        return (
          <Text strong={isHeader} style={{ color: isHeader ? '#1677ff' : '#262626', letterSpacing: 0.5 }}>
            {code}
          </Text>
        );
      },
      sorter: (a: AccountItem, b: AccountItem) => a.code.localeCompare(b.code),
    },
    {
      title: 'Nombre de la Cuenta',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: AccountItem) => {
        const indent = Math.max(0, (record.level - 1) * 16);
        return (
          <div style={{ paddingLeft: indent }}>
            <Text strong={record.level <= 2}>{name}</Text>
          </div>
        );
      },
    },
    {
      title: 'Tipo / Clase',
      dataIndex: 'type',
      key: 'type',
      width: 170,
      render: (type: string) => {
        const conf = TYPE_LABELS[type] || { label: type, color: 'default' };
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: 'Nivel',
      dataIndex: 'level',
      key: 'level',
      width: 90,
      align: 'center' as const,
      render: (level: number) => {
        const labels: Record<number, string> = { 1: 'Clase', 2: 'Grupo', 3: 'Cuenta', 4: 'Subcuenta', 5: 'Auxiliar' };
        return <Tag>{labels[level] || `Niv ${level}`}</Tag>;
      },
    },
    {
      title: 'Naturaleza',
      dataIndex: 'nature',
      key: 'nature',
      width: 110,
      align: 'center' as const,
      render: (nat: string) => (
        <Tag color={nat === 'DEBIT' ? 'blue' : 'purple'}>
          {nat === 'DEBIT' ? 'DÉBITO' : 'CRÉDITO'}
        </Tag>
      ),
    },
    {
      title: 'Tercero',
      dataIndex: 'requires_third_party',
      key: 'requires_third_party',
      width: 100,
      align: 'center' as const,
      render: (req: boolean) => (req ? <Badge status="warning" text="Exigido" /> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      align: 'center' as const,
      render: (active: boolean) => (
        <Badge status={active ? 'success' : 'default'} text={active ? 'Activa' : 'Inactiva'} />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 90,
      align: 'center' as const,
      render: (_: any, record: AccountItem) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleOpenEditModal(record)}
          size="small"
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Plan Único de Cuentas (PUC)</Title>
          <Text type="secondary">Estructura jerárquica de cuentas bajo NIIF / COLGAAP</Text>
        </div>
        <Space wrap>
          {accounts.length === 0 && (
            <Popconfirm
              title="¿Inicializar PUC Estándar?"
              description="Se creará el catálogo base de cuentas colombiano para esta empresa."
              onConfirm={handleSeedPUC}
              okText="Sí, Inicializar"
              cancelText="Cancelar"
            >
              <Button type="dashed" icon={<ThunderboltOutlined />} loading={seeding}>
                Cargar PUC NIIF Estándar
              </Button>
            </Popconfirm>
          )}
          <Button icon={<SyncOutlined spin={accountsValidating || loading} />} onClick={() => mutateAccounts()}>
            Refrescar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
            Nueva Cuenta
          </Button>
        </Space>
      </div>

      {/* Filters Card */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Space wrap size="middle" style={{ width: '100%' }}>
          <Input
            placeholder="Buscar por código o nombre..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />

          <Select
            placeholder="Filtrar por Clase / Tipo"
            style={{ width: 200 }}
            value={selectedType}
            onChange={setSelectedType}
            allowClear
          >
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <Option key={k} value={k}>{v.label}</Option>
            ))}
          </Select>

          <Select
            placeholder="Filtrar por Nivel"
            style={{ width: 160 }}
            value={selectedLevel}
            onChange={setSelectedLevel}
            allowClear
          >
            <Option value={1}>1 - Clase</Option>
            <Option value={2}>2 - Grupo</Option>
            <Option value={3}>3 - Cuenta</Option>
            <Option value={4}>4 - Subcuenta</Option>
            <Option value={5}>5 - Auxiliar</Option>
          </Select>

          <Text type="secondary" style={{ marginLeft: 'auto' }}>
            Mostrando {filteredAccounts.length} de {accounts.length} cuentas
          </Text>
        </Space>
      </Card>

      {/* Accounts Table */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Table
          dataSource={filteredAccounts}
          columns={columns}
          rowKey="code"
          loading={accountsLoading || accountsValidating || loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="middle"
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingAccount ? `Editar Cuenta: ${editingAccount.code}` : 'Crear Nueva Cuenta Contable'}
        open={modalVisible}
        onOk={handleSaveAccount}
        onCancel={() => setModalVisible(false)}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Space style={{ display: 'flex' }} size="middle">
            <Form.Item
              name="code"
              label="Código de Cuenta"
              rules={[{ required: true, message: 'Ingrese el código de la cuenta (ej. 110505)' }]}
              style={{ flex: 1 }}
            >
              <Input
                placeholder="Ej. 110505"
                disabled={!!editingAccount}
                onChange={(e) => {
                  if (!editingAccount) {
                    form.setFieldsValue({ level: calculateAccountLevel(e.target.value) });
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="level"
              label="Nivel Jerárquico"
              rules={[{ required: true, message: 'Seleccione el nivel' }]}
              style={{ flex: 1 }}
              tooltip="Determinado según estándar PUC colombiano"
            >
              <Select disabled={!!editingAccount}>
                <Option value={1}>1 - Clase (1 dígito)</Option>
                <Option value={2}>2 - Grupo (2 dígitos)</Option>
                <Option value={3}>3 - Cuenta (4 dígitos)</Option>
                <Option value={4}>4 - Subcuenta (6 dígitos)</Option>
                <Option value={5}>5 - Auxiliar (8+ dígitos)</Option>
              </Select>
            </Form.Item>
          </Space>

          <Form.Item
            name="name"
            label="Nombre de la Cuenta"
            rules={[{ required: true, message: 'Ingrese el nombre de la cuenta' }]}
          >
            <Input placeholder="Ej. Caja General Principal" />
          </Form.Item>

          <Space style={{ display: 'flex' }} size="middle">
            <Form.Item
              name="type"
              label="Tipo / Clase"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <Option key={k} value={k}>{v.label}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="nature"
              label="Naturaleza Contable"
              rules={[{ required: true }]}
              style={{ flex: 1 }}
            >
              <Select>
                <Option value="DEBIT">DÉBITO (+ por débito, - por crédito)</Option>
                <Option value="CREDIT">CRÉDITO (+ por crédito, - por débito)</Option>
              </Select>
            </Form.Item>
          </Space>

          <Form.Item
            name="parent_id"
            label="Cuenta Superior / Padre"
          >
            <Select placeholder="Seleccionar cuenta padre (opcional)" allowClear showSearch optionFilterProp="children">
              {accounts.map(acc => (
                <Option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Space size="large" style={{ marginTop: 8 }}>
            <Form.Item name="requires_third_party" valuePropName="checked" label="¿Exige Tercero?">
              <Switch />
            </Form.Item>

            <Form.Item name="requires_cost_center" valuePropName="checked" label="¿Centro de Costos?">
              <Switch />
            </Form.Item>

            <Form.Item name="is_active" valuePropName="checked" label="¿Cuenta Activa?">
              <Switch defaultChecked />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default ChartOfAccountsView;
